import React, { Component } from 'react';
import { withRouter } from 'next/router';
import moment from 'moment';
import propTypes from 'prop-types';
import Head from 'next/head';

import errorHandler from 'common/utils/errorHandler';
import _data from 'common/utils/_data';
import withAuth from 'common/providers/withAuth';
import withSubscriptions from 'common/providers/withSubscriptions';
import { ModalHeader, Modal, ModalBody, ModalFooter } from 'components/Modal';
import { EnvBanner } from 'components/EnvBanner';

class HomePage extends Component {
    static displayName = 'HomePage';

    static propTypes = {
        register: propTypes.func,
        login: propTypes.func,
        user: propTypes.object,
        router: propTypes.object,
        createSubscription: propTypes.func,
        getCurrency: propTypes.func,
        currency: propTypes.string,
        locale: propTypes.string,
        getCountry: propTypes.func,
        country: propTypes.string,
        loginWithToken: propTypes.func,
        getCoupons: propTypes.func,
        coupons: propTypes.array,
        coupon: propTypes.object,
        clearCoupon: propTypes.func,
        userLoading: propTypes.bool
    }

    appliedCoupon = ''
    state = {
        step: 1,
        step1Open: true,
        firstName: !Constants.simulate.PREFILL_REGISTRATION ? '' : Constants.prefills.register.firstName,
        lastName: !Constants.simulate.PREFILL_REGISTRATION ? '' : Constants.prefills.register.lastName,
        dob: !Constants.simulate.PREFILL_REGISTRATION ? '' : Constants.prefills.register.dob,
        email: !Constants.simulate.PREFILL_REGISTRATION ? '' : Constants.prefills.register.email,
        confirmEmail: !Constants.simulate.PREFILL_REGISTRATION ? '' : Constants.prefills.register.email,
        password: !Constants.simulate.PREFILL_REGISTRATION ? '' : Constants.prefills.register.password,
        confirmPassword: !Constants.simulate.PREFILL_REGISTRATION ? '' : Constants.prefills.register.confirmPassword,
        loginEmail: !Constants.simulate.PREFILL_LOGIN ? '' : Constants.prefills.login.email,
        loginPassword: !Constants.simulate.PREFILL_LOGIN ? '' : Constants.prefills.login.password,
        studentBeansLoggedIn: Constants.simulate.STUDENTBEANS_LOGGED_IN,
        forgotPasswordCode: '',
        forgotPasswordStep: 1,
        userId: undefined,
        marketing_emails: true,
    }
    static async getInitialProps(ctx) {
        const locale = API.getStoredLocale(ctx.req);
        return { locale };
    }

    componentDidMount() {
        if (this.props.user) {
            API.loggedIn();
        } else {
            const token = Utils.fromParam().ts_token;
            if (token) {
                this.props.loginWithToken(token, {
                    onSuccess: (res) => {
                        API.identify(res.id);
                        API.loggedIn();
                    },
                    onError: (e) => {
                        console.log(e);
                        this.props.router.replace(window.location.pathname);
                    },
                });
            }
        }

        this.props.getCurrency();
        this.props.getCountry();
        this.props.getCoupons();
    }

    onCouponApplied = (appliedCoupon) => {
        this.appliedCoupon = appliedCoupon
    }

    componentDidUpdate(prevProps, prevState) {
        if (!prevState.step2Initiated && this.state.step2Initiated) {
            API.trackEvent('Account Details Initiated');
        }
        if (!prevState.submissionError && this.state.submissionError) {
            this.submissionErrorRef.current.scrollIntoView({
                behavior: 'smooth',
            })
        }

        if (prevProps.user?.token !== this.props.user?.token) {
            API.loggedIn();
        }
    }

    // Do server rendered actions such as fetching data here
    // static async getInitialProps({ Component, ctx }) {
    // }

    onFirstNameChanged = e => this.setState({ firstName: Utils.safeParseEventValue(e), step2Initiated: true });

    onLastNameChanged = e => this.setState({ lastName: Utils.safeParseEventValue(e), step2Initiated: true });

    onDOB = dob => this.setState({ dob, step2Initiated: true });

    onEmailChanged = e => {
        const email = Utils.safeParseEventValue(e);
        this.setState({ email: email ? email.toLowerCase() : '', step2Initiated: true, showConfirmEmailField: true });
    }

    onConfirmEmailChanged = e => {
        const email = Utils.safeParseEventValue(e);
        this.setState({ confirmEmail: email ? email.toLowerCase() : '', step2Initiated: true });
    }

    onPasswordChanged = e => this.setState({ password: Utils.safeParseEventValue(e), step2Initiated: true, showConfirmPasswordField: true });

    onConfirmPasswordChanged = e => this.setState({ confirmPassword: Utils.safeParseEventValue(e), step2Initiated: true });

    toggleLoginModal = () => this.setState({ loginModalOpen: !this.state.loginModalOpen });

    onLoginEmailChanged = e => this.setState({ loginEmail: Utils.safeParseEventValue(e) });

    onLoginPasswordChanged = e => this.setState({ loginPassword: Utils.safeParseEventValue(e) });

    validateAccountDetails = () => {
        const { dob, email, password, confirmPassword, step, firstName, lastName, confirmEmail } = this.state;
        const dobDate = moment(dob, Constants.dateFormats[this.props.currency] || 'MM/DD/YYYY');
        if (dob.length !== 10 || !dobDate.isValid() || dobDate.isSameOrAfter(moment())) {
            this.setState({ createAccountError: 'Please enter a valid date of birth.' });
            return;
        }
        if (password !== confirmPassword) {
            this.setState({ createAccountError: 'Entered passwords do not match', password: '', confirmPassword: '' });
            this.passwordInput.focus();
            return;
        }
        if (!Utils.isValidEmail(email)) {
            this.setState({ createAccountError: 'Please enter a valid email address.' });
            this.emailInput.focus();
            return;
        }
        if (email !== confirmEmail) {
            this.setState({ createAccountError: 'Entered email addresses do not match' });
            this.emailInput.focus();
            return;
        }
        if (step === 2) {
            API.trackEvent('Account Details Entered');
            this.setState({ savedFirstName: firstName, savedLastName: lastName });
            this.nextStep();
            return;
        }
        this.onStep2();
    }

    submit = (stripePayCb) => {
        const { coupons } = this.props;
        const { selectedPlan } = this.state;
        let coupon;
        if (this.props.coupon && Utils.isCouponValidForPlan(this.props.coupon, selectedPlan)) {
            coupon = this.props.coupon;
        } else {
            coupon = Utils.getActiveCoupon(coupons, selectedPlan);
        }
        if (this.props.user) {
            API.logBranchEvent('INITIATE_PURCHASE', {
                revenue: Utils.calculatePriceWithCoupon(coupon, selectedPlan.price),
                currency: this.props.currency,
                coupon: coupon ? coupon.id : '',
                description: selectedPlan.name,
            })
            this.setState({ step1Open: false, step2Open: false, isSubmitting: true, submissionError: null });
            if (stripePayCb) {
                API.trackEvent('Payment Submitted', { 'Payment Type': 'Apple Pay' });
                stripePayCb(true, (e) => {
                    if (e instanceof Error) bugsnagClient.notify(e);
                    console.log(e);
                    this.setState({ isSubmitting: false, submissionError: errorHandler(e) })
                });
                return;
            }
            API.trackEvent('Payment Submitted', { 'Payment Type': 'Card' });

            if (this.paymentRef) {
                this.paymentRef.confirmPayment()
                    .then(authorizedPI => this.onPaymentComplete(authorizedPI))
                    .catch(e => {
                        bugsnagClient.notify(e);
                        API.trackEvent('Payment Failed', { 'Error': errorHandler(e) });
                        console.log(e);
                        this.setState({ isSubmitting: false, submissionError: errorHandler(e) })
                    });
            }
            return;
        }
        const { firstName, lastName, dob, email, password, confirmPassword, marketing_emails, confirmEmail } = this.state;
        const dobDate = moment(dob, Constants.dateFormats[this.props.currency] || 'MM/DD/YYYY');
        if (dob.length !== 10 || !dobDate.isValid() || dobDate.isSameOrAfter(moment())) {
            this.setState({ step2Open: true, createAccountError: 'Please enter a valid date of birth.' });
            return;
        }
        if (password !== confirmPassword) {
            this.setState({ step2Open: true, createAccountError: 'Entered passwords do not match', password: '', confirmPassword: '' });
            this.passwordInput.focus();
            return;
        }
        if (!Utils.isValidEmail(email)) {
            this.setState({ step2Open: true, createAccountError: 'Please enter a valid email address.' });
            this.emailInput.focus();
            return;
        }
        if (email !== confirmEmail) {
            this.setState({ createAccountError: 'Entered email addresses do not match' });
            this.emailInput.focus();
            return;
        }
        this.setState({ step1Open: false, step2Open: false, isSubmitting: true, submissionError: null });
        if (Constants.simulate.SKIP_REGISTRATION) {
            this.paymentRef.confirmPayment()
                .then(authorizedPI => this.onPaymentComplete(authorizedPI))
                .catch(e => {
                    bugsnagClient.notify(e);
                    API.trackEvent('Payment Failed', { 'Error': errorHandler(e) });
                    console.log(e);
                    this.setState({ isSubmitting: false, submissionError: errorHandler(e) })
                });
            return;
        }

        if (!_data.token) {
            _data.post(`${Project.api}/rest-auth/registration/`, {
                first_name: firstName,
                last_name: lastName,
                date_of_birth: dobDate.format('YYYY-MM-DD'),
                email,
                password1: password,
                password2: confirmPassword,
                marketing_emails: !!marketing_emails,
                platform: 'web'
            }).then(async (response) => {
                const res = response.user;
                const idToken = response.key;
                this.setState({ userId: res.id })
                // const cognitoState = await API.authCognitoUser(idToken);
                _data.setToken(idToken);
                API.identify(res.id);
                API.logBranchEvent('COMPLETE_REGISTRATION', {
                    id: res.id,
                    first_name: firstName,
                    last_name: lastName,
                    date_of_birth: dobDate.format('YYYY-MM-DD'),
                    email,
                    marketing_emails: !!marketing_emails,
                });

                if (marketing_emails) API.trackEvent('Opted Into Marketing Emails');
                if (!marketing_emails) API.trackEvent('Opted Out from Marketing Emails');

                API.trackEvent('User Signed Up', {
                    'First Name': firstName,
                    'Last Name': lastName,
                    'Date Of Birth': dobDate.format('DD-MM-YYYY'),
                    'Email': email,
                });
                
                API.setUserRegistrationProperties(email, firstName, lastName, dobDate);
                API.setBrazeCustomAttributes(email, firstName, lastName, dobDate, this.paymentRef.getBillingAddress());
                await API.partnerstackSignup(res.id, firstName, lastName, email);
                // @todo: affiliation?
                API.logBranchEvent('INITIATE_PURCHASE', {
                    revenue: Utils.calculatePriceWithCoupon(coupon, selectedPlan.price),
                    currency: this.props.currency,
                    coupon: coupon ? coupon.id : '',
                    description: selectedPlan.name,
                })
                if (stripePayCb) {
                    API.trackEvent('Payment Submitted', { 'Payment Type': 'Apple Pay' });
                    stripePayCb(true, (e) => {
                        if (e instanceof Error) bugsnagClient.notify(e);
                        console.log(e);
                        this.setState({ isSubmitting: false, submissionError: errorHandler(e) })
                    });
                    return;
                }
                API.trackEvent('Payment Submitted', { 'Payment Type': 'Card' });
                if (this.paymentRef) {
                    this.paymentRef.confirmPayment()
                        .then(authorizedPI => this.onPaymentComplete(authorizedPI))
                        .catch(e => {
                            bugsnagClient.notify(e);
                            API.trackEvent('Payment Failed', { 'Error': errorHandler(e) });
                            console.log(e);
                            this.setState({ email, isSubmitting: false, submissionError: errorHandler(e) })
                        });
                }
            }).catch(e => {
                const errors = JSON.parse(e._bodyText);
                if (stripePayCb) stripePayCb(false);
                if (errors) {
                    if (errors?.email && errors?.email.length && errors?.email[0] === 'A user is already registered with this e-mail address.') {
                        e = 'USER_ALREADY_EXISTS';
                    }
                }
                this.setState({ isSubmitting: false, submissionError: e });
            });
        } else {
            try {
                API.logBranchEvent('INITIATE_PURCHASE', {
                    revenue: Utils.calculatePriceWithCoupon(coupon, selectedPlan.price),
                    currency: this.props.currency,
                    coupon: coupon ? coupon.id : '',
                    description: selectedPlan.name,
                })
                if (stripePayCb) {
                    API.trackEvent('Payment Submitted', { 'Payment Type': 'Apple Pay' });
                    stripePayCb(true, (e) => {
                        if (e instanceof Error) bugsnagClient.notify(e);
                        console.log(e);
                        this.setState({ isSubmitting: false, submissionError: errorHandler(e) })
                    });
                    return;
                }
                API.trackEvent('Payment Submitted', { 'Payment Type': 'Card' });
                if (this.paymentRef) {
                    this.paymentRef.confirmPayment()
                        .then(authorizedPI => this.onPaymentComplete(authorizedPI))
                        .catch(e => {
                            bugsnagClient.notify(e);
                            API.trackEvent('Payment Failed', { 'Error': errorHandler(e) });
                            console.log(e);
                            this.setState({ email, isSubmitting: false, submissionError: errorHandler(e) })
                        });
                }
            } catch (e) {
                if (stripePayCb) stripePayCb(false);
                this.setState({ isSubmitting: false });
            }
        }
    }

    canCreateAccount = () => {
        const { firstName, lastName, dob, email, password, confirmPassword } = this.state;
        return firstName && lastName && dob && email && password && confirmPassword;
    }

    login = (e) => {
        if (e) Utils.preventDefault(e);
        const { loginEmail, loginPassword } = this.state;
        this.setState({ loginError: null });
        this.props.login({
            email: loginEmail,
            password: loginPassword,
        }, {
            onSuccess: (res) => {
                API.identify(res.id);
                API.logBranchEvent('LOGIN', {
                    id: res.id,
                    email: loginEmail,
                });
                API.loggedIn();
            },
            onError: (e) => {
                console.log(e);
                this.setState({ loginError: e });
            },
        });
    }

    onStep1 = () => {
        const { step, isSubmitting, step1Open, step1Opened } = this.state;
        if (step === 1 || isSubmitting) return;
        // if (!step1Open && !step1Opened) API.trackEvent('Toggle Subscription');
        this.setState({ step1Open: !step1Open, step1Opened: true });
    }

    onStep2 = () => {
        const { step, isSubmitting } = this.state;
        if (step <= 2 || isSubmitting) return;
        this.setState({ step2Open: !this.state.step2Open });
    }

    nextStep = () => this.setState({ step: this.state.step + 1, step1Open: this.state.step + 1 === 2 ? false : this.state.step1Open });

    onPaymentComplete = (paymentIntent, stripePay) => {
        if (paymentIntent?.gateway_account_id) delete paymentIntent.gateway_account_id;
        if (stripePay && this.submissionContainerRef) this.submissionContainerRef.current.scrollIntoView({ behavior: 'smooth' });
        const { selectedPlan, firstName, lastName, studentBeansLoggedIn } = this.state;
        const { currency, coupons, user } = this.props;
        const payload = {
            plan_id: selectedPlan.id,
            payment_intent: paymentIntent,
        };
        if (currency !== 'USD') {
            payload.billing_address = this.paymentRef.getBillingAddress();
        }
        if (stripePay) {
            payload.payment_intent = {
                gw_token: paymentIntent.id,
            }
        }
        let coupon;
        if (this.props.coupon && Utils.isCouponValidForPlan(this.props.coupon, selectedPlan)) {
            coupon = this.props.coupon;
        } else {
            coupon = Utils.getActiveCoupon(coupons, selectedPlan);
        }
        if (coupon && this.appliedCoupon) {
            payload.coupon_ids = [this.appliedCoupon];
            // if (selectedPlan.id.includes('student') && coupon.duration_type === 'one_time') {
            //     const foreverStudentCoupons = _.filter(this.props.coupons, coupon => coupon.status === 'active' && coupon.duration_type === 'forever');
            //     if (foreverStudentCoupons.length) payload.coupon_ids.push(foreverStudentCoupons[0].id);
            // }
        }

        this.props.createSubscription(payload, {
            onSuccess: () => {
                this.props.clearCoupon();
                const eventData = { 'Subscription Name': selectedPlan.name };
                API.trackDiscountApplied(coupon, eventData);
                API.trackEvent('Subscription Completed: Web', eventData);
                // @todo: affiliation?
                const revenue = Utils.calculatePriceWithCoupon(coupon, selectedPlan.price)

                API.logBranchEvent('PURCHASE', {
                    revenue,
                    currency,
                    coupon: coupon ? coupon.id : '',
                    description: selectedPlan.name,
                })
                if (window.appboy) {
                    appboy.logPurchase(selectedPlan.id, revenue, currency, 1, {
                        coupon: coupon ? coupon.id : '',
                        description: selectedPlan.name,
                        userEmail: this.state.email,
                    })
                }
                if (selectedPlan.trial_period && selectedPlan.trial_period > 0) {
                    API.logBranchEvent('START_TRIAL', {
                        description: 'Chargebee subscription trial period started',
                        trial_period: selectedPlan.trial_period,
                        trial_period_unit: selectedPlan.trial_period_unit,
                    });
                }

                this.setState({ isSubmitting: false, subscribed: true });
            },
            onError: (e) => {
                bugsnagClient.notify(e);
                API.trackEvent('Subscription Failed: Web', { 'Subscription Name': selectedPlan.name, 'Error': errorHandler(e) });
                this.setState({ isSubmitting: false, submissionError: errorHandler(e) });
            }
        });
    }

    selectPlan = (plan, autoSelected) => {
        // if (!autoSelected) API.trackEvent('Subscription Selected in Subscription Funnel', { 'Subscription Name': plan.name });
        this.setState({ selectedPlan: plan });
        if (this.state.step === 1 && !autoSelected) {
            this.nextStep();
        } else {
            this.setState({ step1Open: false });
        }
    }

    goToAccount = () => {
        API.trackEvent('View Subscription');
        this.props.router.replace('/account');
    }

    toggleMarketingEmails = () => {
        this.setState({ marketing_emails: !this.state.marketing_emails });
    }

    studentBeansLoggedIn = () => this.setState({ studentBeansLoggedIn: true });

    submissionErrorRef = React.createRef();
    submissionContainerRef = React.createRef();

    forgotPassword = () => {
        this.setState({
            loginModalOpen: false, forgotPasswordModalOpen: true, forgotPasswordCode: '', forgotPasswordStep: 1,
            forgotPasswordPassword: '', forgotPasswordConfirmPassword: ''
        });
    }

    toggleForgotPasswordModal = () => this.setState({ forgotPasswordModalOpen: false });

    submitForgotPassword = (e) => {
        if (e) e.preventDefault();
        this.setState({ forgotPasswordError: '' });
        _data.post(`${Project.api}/rest-auth/password/reset/`, { email: this.state.loginEmail })
            .then(() => {
                this.setState({ forgotPasswordStep: 2 });
            })
            .catch(e => {
                this.setState({ forgotPasswordError: errorHandler(e) });
            });
    }

    onForgotPasswordCodeChanged = (e) => this.setState({ forgotPasswordCode: Utils.safeParseEventValue(e) });

    onForgotPasswordPasswordChanged = e => this.setState({ forgotPasswordPassword: Utils.safeParseEventValue(e) });

    onForgotPasswordConfirmPasswordChanged = e => this.setState({ forgotPasswordConfirmPassword: Utils.safeParseEventValue(e) });

    confirmForgotPassword = (e) => {
        if (e) e.preventDefault();
        this.setState({ forgotPasswordError: '' });
        const { loginEmail: email, forgotPasswordCode: token, forgotPasswordPassword: new_password1, forgotPasswordConfirmPassword: new_password2 } = this.state;
        _data.post(`${Project.api}/rest-auth/password/reset/confirm/`, {
            email,
            token,
            new_password1,
            new_password2,
        })
            .then(() => {
                this.setState({ forgotPasswordStep: 3 });
            })
            .catch(e => {
                let error = errorHandler(e);
                if (error === 'Invalid value') {
                    error = 'Invalid code. Please check you entered it correctly.';
                }
                this.setState({ forgotPasswordError: error });
            });
    }

    goToLoginFromForgotPassword = (e) => {
        if (e) e.preventDefault();
        this.setState({ forgotPasswordModalOpen: false, loginModalOpen: true });
        if (this.loginPasswordInput) this.loginPasswordInput.focus();
    }

    openLoginModalForAlreadyRegisteredUser = () => {
        this.setState({ loginEmail: this.state.email, loginModalOpen: true });
    }

    render() {
        const {
            step, firstName, lastName, dob, email, password, confirmPassword,
            submissionError, loginModalOpen, loginEmail, loginPassword, loginError,
            step1Open, step2Open, selectedPlan, isSubmitting, subscribed, marketing_emails,
            createAccountError, studentBeansLoggedIn, savedFirstName, savedLastName,
            forgotPasswordModalOpen, forgotPasswordError, forgotPasswordStep, forgotPasswordCode,
            forgotPasswordPassword, forgotPasswordConfirmPassword, confirmEmail, showConfirmEmailField,
            showConfirmPasswordField,
            userId,
        } = this.state;
        
        const { user, currency, country, coupons, coupon, userLoading, locale } = this.props;

        const userIsNotSubscribed = (!subscribed && !Constants.simulate.FORCE_SUBSCRIBE_COMPLETE)

        return !userLoading && (
            <div className="main-container">
                <nav className="flex-row topbar align-flex-end">
                    <div className="topbar__inner">
                        <div className="topbar__items">
                            <a className="logo" href="https://www.evolveyou.app">
                                <img src="https://d2bbts1v7ku976.cloudfront.net/img/logo.svg" alt="EvolveYou" />
                            </a>
                        </div>
                    </div>
                </nav>
                <div className="flex-row align-items-start flex-column-reverse flex-lg-row">
                    <div className="col-12 col-lg-4 pb-4">
                        {(
                            <div className="align-items-start pb-3">
                                <div className="px-2 px-xl-5 mt-5 ">
                                    <div className="centered-container pt-3">
                                        <div id="home-page" className="flex-row justify-content-center align-items-center">
                                            <span className="text-center mt-5">
                                                <b>Include a 7 day free trial</b>
                                            </span>
                                            <a href={`${Project.stripeUrl}`} className="btn--primary text-center mt-4">
                                                Confirm Payment
                                            </a>
                                            <a className="text-center text-medium mt-4" href={`${Project.loginPage}?redirect_url=${Project.selfUrl}`}>
                                                Already a member ? Login
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="col-12 col-lg-8 feature-gfx p-2 p-lg-4">
                        <div className="flex-row justify-content-between justify-content-between justify-content-sm-end">
                            <div className="flex hidden-sm-up">
                                {/* <img className="brand" src="/static/images/brand-white.svg" alt="EvolveYou"
                                  className="hidden-sm-up"/> */}
                            </div>
                        </div>
                    </div>
                </div>
                <Modal isOpen={loginModalOpen} toggle={this.toggleLoginModal}>
                    <ModalHeader toggle={this.toggleLoginModal}>Login</ModalHeader>
                    <form>
                        <ModalBody>
                            <Column className="flex-1">
                                <div className="input-container mb-3">
                                    <div className="flex-row">
                                        <img src="/static/images/icons/email.svg" alt="email" className="icon px-3"/>
                                        <input
                                          className="input input--transparent flex-1" placeholder="Email" value={loginEmail}
                                          onChange={this.onLoginEmailChanged} type="email" ref={c => this.loginEmailInput = c}
                                    />
                                    </div>
                                </div>
                                <div className="input-container mb-3">
                                    <div className="flex-row">
                                        <img src="/static/images/icons/lock.svg" alt="lock" className="icon px-3"/>
                                        <input
                                          className="input input--transparent flex-1" placeholder="Password" value={loginPassword}
                                          onChange={this.onLoginPasswordChanged} type="password" ref={c => this.loginPasswordInput = c}
                                    />
                                    </div>
                                </div>
                                <p className="link-style clickable text-center" role="button" onClick={this.forgotPassword}>Forgot password?</p>
                                {!!loginError && <ErrorMessage className="mt-3">{loginError}</ErrorMessage>}
                            </Column>
                        </ModalBody>
                        <ModalFooter>
                            <div className="flex-row flex-1 align-content-between">
                                <div className="flex-fill px-1">
                                    <Button className="btn--outline-blue full-width" onClick={this.toggleLoginModal}>Cancel</Button>
                                </div>
                                <div className="flex-fill px-1">
                                    <Button type="submit" className="btn--primary full-width" onClick={this.login}
                                      disabled={!loginEmail || !loginPassword}>Login</Button>
                                </div>
                            </div>
                        </ModalFooter>
                    </form>
                </Modal>
                <Modal isOpen={forgotPasswordModalOpen} toggle={this.toggleForgotPasswordModal}>
                    <ModalHeader toggle={this.toggleForgotPasswordModal}>Forgot Password</ModalHeader>
                    <form>
                        <ModalBody>
                            <Column className="flex-1">
                                {forgotPasswordStep === 3 ? (
                                    <>
                                        <p>Password reset successfully!</p>
                                    </>
                                ) : forgotPasswordStep === 2 ? (
                                    <>
                                        <p>Please check your email for the confirmation code to continue with resetting your password.</p>
                                        <div className="input-container mb-3">
                                            <div className="flex-row">
                                                <img src="/static/images/icons/lock.svg" alt="email" className="icon px-3"/>
                                                <input
                                                  className="input input--transparent flex-1" placeholder="Code" value={forgotPasswordCode}
                                                  onChange={this.onForgotPasswordCodeChanged} type="email"
                                                />
                                            </div>
                                        </div>
                                        <div className="input-container mb-3">
                                            <div className="flex-row">
                                                <img src="/static/images/icons/lock.svg" alt="lock" className="icon px-3"/>
                                                <input
                                                  className="input input--transparent flex-1" placeholder="Password" value={forgotPasswordPassword}
                                                  onChange={this.onForgotPasswordPasswordChanged} type="password"
                                                />
                                            </div>
                                        </div>
                                        <div className="input-container">
                                            <div className="flex-row">
                                                <img src="/static/images/icons/lock.svg" alt="lock" className="icon px-3"/>
                                                <input
                                                  className="input input--transparent flex-1" placeholder="Confirm Password" value={forgotPasswordConfirmPassword}
                                                  onChange={this.onForgotPasswordConfirmPasswordChanged} type="password"
                                                />
                                                {(forgotPasswordPassword && forgotPasswordConfirmPassword && forgotPasswordPassword === forgotPasswordConfirmPassword) ? <ion-icon name="checkmark-circle-outline" class={`icon-small mr-2 icon--green`}></ion-icon> : null}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="input-container mb-3">
                                        <div className="flex-row">
                                            <img src="/static/images/icons/email.svg" alt="email" className="icon px-3"/>
                                            <input
                                              className="input input--transparent flex-1" placeholder="Email" value={loginEmail}
                                              onChange={this.onLoginEmailChanged} type="email"
                                        />
                                        </div>
                                    </div>
                                )}
                                {!!forgotPasswordError && <ErrorMessage className="mt-3">{forgotPasswordError}</ErrorMessage>}
                            </Column>
                        </ModalBody>
                        <ModalFooter>
                            <div className="flex-row flex-1 align-content-between">
                                {forgotPasswordStep !== 3 && (
                                    <div className="flex-fill px-1">
                                        <Button className="btn--outline-blue full-width" onClick={this.toggleForgotPasswordModal}>Cancel</Button>
                                    </div>
                                )}
                                <div className="flex-fill px-1">
                                    <Button type="submit" className="btn--primary full-width" onClick={forgotPasswordStep === 1 ? this.submitForgotPassword : forgotPasswordStep === 3 ? this.goToLoginFromForgotPassword : this.confirmForgotPassword}
                                      disabled={!loginEmail}>{forgotPasswordStep === 3 ? 'Login' : 'OK'}</Button>
                                </div>
                            </div>
                        </ModalFooter>
                    </form>
                </Modal>
                <EnvBanner />
            </div>
        );
    }
}

export default withRouter(withAuth(withSubscriptions(HomePage)));
